// This module contains the complete cell customization logic you had originally, converted to pure functions
import { PDF_STYLES } from './pdfStyles';

// eslint-disable-next-line no-unused-vars
export function createCellCustomizer({ formatCellValue, fontScale = 1 }) {
    return function cellCustomizer(options) {
        const excelCell = options.pdfCell || options.excelCell;
        excelCell.font = { size: PDF_STYLES.defaultFontSize * fontScale };
        excelCell.padding = 1;


        const gridCell = options.gridCell || {};
        // 'group' is the group header row; 'groupFooter'/'totalFooter' are the per-group and
        // grand-total subtotal rows — distinct row types that need the same summary-row
        // treatment (bold, scaled size, no numeric wrap), not just 'group'.
        const isSummaryRow = gridCell.rowType === 'group' || gridCell.rowType === 'groupFooter' || gridCell.rowType === 'totalFooter';

        // Numeric values (quantities, amounts) shouldn't wrap onto multiple lines — a wrapped
        // number reads as broken/ambiguous in a way wrapped text doesn't, and a narrow column
        // width says nothing about how wide the values in it actually are.
        if (gridCell.column && gridCell.column.dataType === 'number' && gridCell.rowType === 'data') {
            excelCell.wordWrapEnabled = false;
        }

        // Summary rows (group/subtotal/grand-total) get the same treatment unconditionally,
        // not gated on the cell's own column dataType — when a group has no per-column inline
        // summaries, DevExtreme merges the whole row into a single cell keyed to the *grouped*
        // column (e.g. "Branch", a text column), not the numeric columns whose totals it's
        // summarizing (see rows_generator.js's isEmptyCellsExceptFirst merge). Gating on
        // dataType === 'number' never matches that merged cell even though its text is exactly
        // the kind of short one-line summary that shouldn't wrap.
        if (isSummaryRow) {
            excelCell.wordWrapEnabled = false;
        }


        if (gridCell.rowType === 'data') {
            if (gridCell.column && gridCell.column.cellTemplate === 'ActiveStatus') {
                if (gridCell.value === 'N' || !gridCell.value) excelCell.value = 'Active';
                else if (gridCell.value === 'Y' || gridCell.value) excelCell.value = 'Inactive';
            } else if (gridCell.column && gridCell.column.dataType === 'datetime') {
                if (gridCell.value)
                    excelCell.value = (typeof gridCell.value.dateWithOffset === 'function')
                        ? (gridCell.value.dateWithOffset().toShortDateString() + ', ' + gridCell.value.dateWithOffset().toShortTimeString())
                        : (new Date(gridCell.value).toLocaleString());
            } else if (gridCell.column && gridCell.column.cellTemplate === 'RadioTemplate') {
                if (gridCell.value === 'N' || !gridCell.value) excelCell.value = 'No';
                else if (gridCell.value === 'Y' || gridCell.value) excelCell.value = 'Yes';
            } else if (gridCell.column && gridCell.column.dataType === 'date' && gridCell.column.format === 'dd MMM yyyy') {
                if (gridCell.value) excelCell.value = new Date(gridCell.value).toLocaleDateString();
            }
        }


        // Percentage / formatting logic
        var pattern = /[%]/g;
        if (gridCell.column && gridCell.column.format) {
            if (gridCell.value) {
                if (pattern.test(gridCell.column.format) && typeof gridCell.value === 'number') {
                    excelCell.value = gridCell.value.toFixed(2) + ' %';
                } else {
                    excelCell.numFmt = gridCell.column.format;
                }


                if (typeof gridCell.value === 'number' && gridCell.value <= 0) {
                    // Merge, don't replace — a plain assignment here would silently drop the
                    // `size`/`bold` set above (that's what was breaking font size on negative
                    // values, including group/total cells further down).
                    excelCell.font = Object.assign({}, excelCell.font, { color: { argb: PDF_STYLES.negativeColorArgb } });
                    excelCell.numFmt = gridCell.column.format;
                    excelCell.textColor = PDF_STYLES.negativeHex;
                }
            } else {
                if (gridCell.value == 0) {
                    excelCell.value = '-';
                } else {
                    if (gridCell.rowType == 'group' && gridCell.groupSummaryItems) {
                        if (pattern.test(gridCell.column.format))
                            excelCell.value = gridCell.groupSummaryItems[0].value.toFixed(2) + ' %';
                        else {
                            excelCell.numFmt = gridCell.column.format;
                            excelCell.value = gridCell.groupSummaryItems[0].value;
                        }
                        if (gridCell.groupSummaryItems[0].value < 0) {
                            excelCell.font = Object.assign({}, excelCell.font, { color: { argb: PDF_STYLES.negativeColorArgb }, bold: true });
                        } else if (gridCell.groupSummaryItems[0].value == 0) {
                            excelCell.value = '-';
                        }
                    }
                }
            }
        }

        if (excelCell.text) {
            const hasBrTag = /<br\s*\/?>/i.test(excelCell.text);
            if (hasBrTag) {
                excelCell.text = excelCell.text.replace(/<br\s*\/?>/gi, '');
                // Additional '\n' is not requited as this point since text already wraps with '\n' in the text when exporting to PDF. If we add additional '\n' here, it will create extra line breaks in the PDF export. So we can just remove the <br> tags without replacing them with '\n'.
            }
        }


        // group row background
        if (gridCell.rowType === 'group') {
            excelCell.backgroundColor = PDF_STYLES.groupBg;
        }


        excelCell.borderWidth = 0.1;
        excelCell.borderColor = '#ffffff';

        // Detail (plain data) rows get a dotted bottom border instead of the invisible default
        // above — but DevExtreme's own border drawing (draw_utils.js) only ever draws solid
        // lines, no dashed/dotted style exists at that layer. The dotted line itself is drawn by
        // hand in pdfExporter.js's customDrawCell; suppressing the default solid bottom border
        // here just stops it from rendering underneath/alongside the hand-drawn one. No-op for
        // Excel (ExcelJS's cell border model doesn't recognize this property).
        if (gridCell.rowType === 'data') {
            excelCell.drawBottomBorder = false;
            // Native PDF cell property (pdf_exporter.d.ts's Cell.verticalAlign) — DevExtreme
            // defaults to vertically centering text within a cell (drawTextInRect in
            // draw_utils.js); 'top' aligns it to the top instead, matching how a wrapped
            // multi-line value in a taller row (next to shorter single-line values) should read.
            // No-op for Excel (ExcelJS's alignment model is a nested `alignment.vertical`, not
            // this flat property).
            excelCell.verticalAlign = 'top';
        }


        if (gridCell.rowType === 'header') {
            excelCell.font = { bold: true, size: (PDF_STYLES.defaultFontSize + PDF_STYLES.headerGroupFontSizeBoost) * fontScale, color: { argb: PDF_STYLES.negativeColorArgb } };
            excelCell.backgroundColor = PDF_STYLES.headerBg;
            excelCell.textColor = PDF_STYLES.headerTextColor;
            // DevExtreme's PDF row height is derived purely from text height + cell padding
            // (there's no direct row-height option reachable from customizeCell) — so taller
            // top/bottom padding here is what actually makes the header row read taller in PDF.
            // Left/right stay at the default 1pt; only vertical padding needs to grow.
            excelCell.padding = { top: PDF_STYLES.headerRowPaddingY, right: 1, bottom: PDF_STYLES.headerRowPaddingY, left: 1 };
        }


        if (isSummaryRow) {
            excelCell.font = { bold: true, size: (PDF_STYLES.defaultFontSize + PDF_STYLES.headerGroupFontSizeBoost) * fontScale };


            if (excelCell.value <= 0) {
                // Merge, not replace, so `size`/`bold` set just above survive.
                excelCell.font = Object.assign({}, excelCell.font, { color: { argb: PDF_STYLES.negativeColorArgb } });
                excelCell.numFmt = gridCell.column ? gridCell.column.format : undefined;
                excelCell.textColor = PDF_STYLES.negativeHex;
            }
        }
    };
}